// pipe (scan-hardening): ONE camera barcode decoder behind the Shape Detection API's `detect`
// shape. Chromium/Android provide a native `BarcodeDetector`; iOS/WebKit (the Capacitor iPhone
// app + Safari — mobile-first is a hard requirement) do not, so a pure-JS ZXing reader is
// LAZY-LOADED as the fallback (a separate chunk fetched only when a camera scan actually starts
// on a platform without the native API — the gun/typing path never pays for it). Not a DI module
// (the labels-module convention); the ScanDialog awaits `createBarcodeDetector()` when the
// camera turns on.

/** The (minimal) Shape Detection API surface the scan camera loop consumes. */
export interface DetectedBarcode {
  rawValue: string;
}
export interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}

type BarcodeDetectorCtor = new () => BarcodeDetectorLike;

/** Whether SOME camera decode path exists here: camera capture is required either way; the
 *  decoder is native where available, else the lazy ZXing fallback (always constructible). */
export function cameraDecodeSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!globalThis.navigator?.mediaDevices?.getUserMedia
  );
}

/** ZXing fallback: draw the current video frame onto a (reused) canvas and one-shot decode it.
 *  A per-frame miss is the NORMAL case (ZXing throws NotFoundException) → empty array, exactly
 *  like a native detect() with no hit; the dialog's ~200ms poll provides the retry cadence. */
class ZxingDetector implements BarcodeDetectorLike {
  private readonly canvas = document.createElement('canvas');
  constructor(
    private readonly reader: {
      decodeFromCanvas(canvas: HTMLCanvasElement): { getText(): string };
    }
  ) {}

  detect(video: HTMLVideoElement): Promise<DetectedBarcode[]> {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return Promise.resolve([]);
    this.canvas.width = width;
    this.canvas.height = height;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return Promise.resolve([]);
    ctx.drawImage(video, 0, 0, width, height);
    try {
      const text = this.reader.decodeFromCanvas(this.canvas).getText();
      return Promise.resolve(text ? [{ rawValue: text }] : []);
    } catch {
      return Promise.resolve([]); // no barcode in this frame — keep polling
    }
  }
}

/** The decoder for this platform: native `BarcodeDetector` where the browser ships one, else the
 *  lazy-loaded ZXing reader. `null` only when the fallback chunk itself fails to load (offline
 *  mid-session) — the caller degrades to the "use the gun / type it" hint, never a crash. */
export async function createBarcodeDetector(): Promise<BarcodeDetectorLike | null> {
  const Native = (window as { BarcodeDetector?: BarcodeDetectorCtor })
    .BarcodeDetector;
  if (Native) {
    try {
      return new Native();
    } catch {
      // Constructible check failed (no supported formats?) — fall through to ZXing.
    }
  }
  try {
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    return new ZxingDetector(new BrowserMultiFormatReader());
  } catch {
    return null;
  }
}
